import React, { useMemo, useState, useEffect, useContext } from 'react';
import { ThemeContext } from 'styled-components';
import { format } from 'date-fns';
import { getThingTrips, getThingBlocks } from '../../services';
import { dateFormats } from '../../constants';
import Icon from '../icon/icon';
import * as s from './styled-event-timeline';

// EventTimeline — faixa horizontal estilo "day-bar" mostrando todos os
// trips e blocks de uma maquina dentro de um periodo (default: ultimas 24h).
// Funciona como contexto pra o LineChart: o operador ve "houve evento as 14h"
// e correlaciona com a queda na curva.
//
// Props:
//   - thingId: id da maquina selecionada
//   - period: 'daily' | 'weekly' | 'monthly' — define a janela exibida
//   - onEventClick(event): callback quando operador clica num marcador
//   - eventsExternal: opcional — array de eventos ja carregado pelo pai
//     (ex.: LineChart). Quando informado, EventTimeline NAO faz fetch
//     proprio (evita duplicacao). Cada item deve ter { ts, type, reason }.
//
// Fetches trips + blocks em paralelo SO se eventsExternal nao for informado.
const EventTimeline = ({
  thingId,
  period = 'daily',
  onEventClick,
  eventsExternal,
}) => {
  const { colors } = useContext(ThemeContext);
  const [trips, setTrips] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(false);

  // Skip fetch quando o pai ja passou os eventos prontos (dedup).
  const skipFetch = Array.isArray(eventsExternal);

  useEffect(() => {
    if (skipFetch) return undefined;
    if (!thingId) return undefined;
    let mounted = true;
    setLoading(true);

    Promise.all([
      getThingTrips(thingId, 1, 100).catch(() => ({ data: [] })),
      getThingBlocks(thingId, 1, 100).catch(() => ({ data: [] })),
    ])
      .then(([tripsResp, blocksResp]) => {
        if (!mounted) return;
        // API pode retornar array direto OU { data: [...] } OU { data: { items: [...] }}.
        // Normaliza pra array plano com timestamp + reason.
        const flatten = (resp) => {
          const raw = (resp && resp.data) || [];
          if (Array.isArray(raw)) return raw;
          if (Array.isArray(raw.items)) return raw.items;
          return [];
        };
        setTrips(flatten(tripsResp));
        setBlocks(flatten(blocksResp));
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [thingId, skipFetch]);

  // Janela temporal: daily = ultimas 24h, weekly = 7d, monthly = 30d.
  // Eventos fora desta janela sao filtrados.
  const windowMs = useMemo(() => {
    if (period === 'monthly') return 30 * 24 * 60 * 60 * 1000;
    if (period === 'weekly') return 7 * 24 * 60 * 60 * 1000;
    return 24 * 60 * 60 * 1000;
  }, [period]);

  const events = useMemo(() => {
    const now = Date.now();
    const cutoff = now - windowMs;

    // Caminho A: pai passou eventos prontos. Filtramos por janela e
    // padronizamos o formato (alguns vem como { ts, type, reason }).
    if (skipFetch) {
      return eventsExternal
        .filter(
          (e) => e && Number.isFinite(e.ts) && e.ts >= cutoff,
        )
        .sort((a, b) => a.ts - b.ts);
    }

    // Caminho B: fetch local. Extrai timestamp do evento — backend pode
    // usar 'createdAt', 'ts', 'timestamp' ou epoch numerico. Defensivo.
    const parseEventTime = (e) => {
      const candidates = [e.createdAt, e.ts, e.timestamp, e.date];
      let found = null;
      candidates.some((c) => {
        if (!c) return false;
        const t =
          typeof c === 'number'
            ? c < 1e12 ? c * 1000 : c // segundos vs ms
            : new Date(c).getTime();
        if (Number.isFinite(t)) {
          found = t;
          return true;
        }
        return false;
      });
      return found;
    };

    const enriched = [];
    trips.forEach((t) => {
      const ts = parseEventTime(t);
      if (ts && ts >= cutoff)
        enriched.push({
          ts,
          type: 'trip',
          reason: t.reason || t.description || t.name || 'Trip',
          raw: t,
        });
    });
    blocks.forEach((b) => {
      const ts = parseEventTime(b);
      if (ts && ts >= cutoff)
        enriched.push({
          ts,
          type: 'block',
          reason: b.reason || b.description || b.name || 'Block',
          raw: b,
        });
    });
    return enriched.sort((a, b) => a.ts - b.ts);
  }, [trips, blocks, windowMs, eventsExternal, skipFetch]);

  // Faixa exibida — sempre [now - windowMs, now]. Posicao percentual de
  // cada evento e calculada relativa a essa janela.
  const now = Date.now();
  const start = now - windowMs;

  const positionFor = (ts) => ((ts - start) / windowMs) * 100;

  if (!thingId) return null;

  // Estado vazio (sem eventos no periodo) colapsa pra uma linha fina —
  // libera espaco no chart. Operador ainda ve o label 'Eventos: 0 no
  // periodo' mas sem o strip vazio gigante.
  const isEmpty = !loading && events.length === 0;
  if (isEmpty) {
    return (
      <s.Container compact>
        <s.Label>
          Eventos
          <s.EmptyHintInline>0 no período</s.EmptyHintInline>
        </s.Label>
      </s.Container>
    );
  }

  return (
    <s.Container>
      <s.Label>
        Eventos
        {events.length > 0 && (
          <s.EventCount>{events.length}</s.EventCount>
        )}
      </s.Label>
      <s.Strip>
        {/* Ticks de referencia (4 pra daily, ~7 pra weekly, ~4 pra monthly) */}
        {[0, 25, 50, 75, 100].map((pct) => (
          <s.Tick key={pct} $position={pct} />
        ))}

        {loading && <s.LoadingPlaceholder>Carregando eventos…</s.LoadingPlaceholder>}

        {events.map((evt) => {
          const pos = positionFor(evt.ts);
          if (pos < 0 || pos > 100) return null;
          const color =
            evt.type === 'block' ? colors.red : colors.yellow;
          return (
            <s.Marker
              key={`evt-${evt.type}-${evt.ts}-${evt.reason}`}
              $position={pos}
              $color={color}
              onClick={() => onEventClick && onEventClick(evt)}
              title={`${evt.type === 'block' ? 'Bloqueio' : 'Trip'}: ${
                evt.reason
              } — ${format(new Date(evt.ts), dateFormats.DATE_TIME_SLASH)}`}
            >
              <Icon
                name={evt.type === 'block' ? 'ban' : 'alert-triangle'}
                size={12}
              />
            </s.Marker>
          );
        })}
      </s.Strip>
      <s.AxisLabels>
        <span>
          {format(new Date(start), period === 'daily' ? 'HH:mm' : 'dd/MM')}
        </span>
        <span>
          {format(new Date(now), period === 'daily' ? 'HH:mm' : 'dd/MM')}
        </span>
      </s.AxisLabels>
    </s.Container>
  );
};

export default EventTimeline;
